import type { Schema } from "effect"

// A topic family is one kind of live stream over the WebSocket (docs/design/0005,
// "WebSocket"): its topic schema (what a client subscribes to), how a topic maps
// to its key, and the schemas of the data it carries. Families are data in the
// TOPIC_FAMILIES registry (topics/index.ts); WsProtocol derives its unions from it.

/** A family's topic: a tagged struct whose `_tag` names the family, e.g. Account{accountId}. */
export type TopicSchema = Schema.Codec<{ readonly _tag: string }, unknown, never, never>

/** Snapshot, update or status data. Service-free, so the hub encodes it synchronously. */
export type TopicDataSchema = Schema.Codec<unknown, unknown, never, never>

export interface TopicFamily<
  T extends TopicSchema = TopicSchema,
  S extends TopicDataSchema = TopicDataSchema,
  U extends TopicDataSchema = TopicDataSchema,
  St extends TopicDataSchema = TopicDataSchema
> {
  /** The topic's `_tag`, unique across families. */
  readonly tag: string
  readonly topic: T
  /**
   * The topic's key, unique across all topics, e.g. "account:acc_1". Frames carry
   * it instead of a per-connection id, so one serialized frame serves every subscriber.
   * (Method syntax on purpose: families of different topics share one registry list.)
   */
  key(topic: T["Type"]): string
  /** Full state, sent once per subscriber (reliable topics). */
  readonly snapshot: S
  /** One change; Update frames are numbered seq = previous + 1, gap-free. */
  readonly update: U
  /** Latest-wins data, coalesced (≤ 4/s). Each one replaces the previous entirely. */
  readonly status: St
}

/** Declares a family with its schema types inferred. */
export const topicFamily = <T extends TopicSchema, S extends TopicDataSchema, U extends TopicDataSchema, St extends TopicDataSchema>(
  family: TopicFamily<T, S, U, St>
): TopicFamily<T, S, U, St> => family

export type TopicOf<F extends TopicFamily> = F["topic"]["Type"]
export type SnapshotOf<F extends TopicFamily> = F["snapshot"]["Type"]
export type UpdateOf<F extends TopicFamily> = F["update"]["Type"]
export type StatusOf<F extends TopicFamily> = F["status"]["Type"]
